import { Drawer, Modal } from '@heroui/react';
import type { ReactNode } from 'react';
import { createContext, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import usePlatform from '@/hooks/use-platform';
import useScrimThemeColor from '@/hooks/use-scrim-theme-color';
import Button from '@/shared/components/Button';
import type { WalletOption } from '@/utils/wallet/evm';

type WalletModalContextType = {
  show: (wallets: WalletOption[]) => Promise<WalletOption | undefined>;
};

export const WalletModalContext = createContext<WalletModalContextType>({
  show: async () => {
    throw new Error('WalletModalProvider is not mounted');
  },
});

type Props = {
  readonly children?: ReactNode;
};

const iconPrefixes = ['data:image/', 'https://'];

const isSafeIcon = (icon?: string) => iconPrefixes.some((prefix) => icon?.startsWith(prefix));

const iconClassName = 'me-3 size-8 shrink-0 rounded-lg';

const WalletModalProvider = ({ children }: Props) => {
  const { t } = useTranslation();
  const { isMobile } = usePlatform();
  const [wallets, setWallets] = useState<WalletOption[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const resolverRef = useRef<((wallet?: WalletOption) => void) | undefined>(undefined);
  useScrimThemeColor(isOpen);

  const show = useCallback(async (options: WalletOption[]) => {
    resolverRef.current?.(undefined);
    setWallets(options);
    setIsOpen(true);

    return new Promise<WalletOption | undefined>((resolve) => {
      // eslint-disable-next-line @silverhand/fp/no-mutation -- store the pending modal resolver
      resolverRef.current = resolve;
    });
  }, []);

  const close = useCallback((wallet?: WalletOption) => {
    resolverRef.current?.(wallet);
    // eslint-disable-next-line @silverhand/fp/no-mutation -- clear the settled modal resolver
    resolverRef.current = undefined;
    setIsOpen(false);
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        close();
      }
    },
    [close]
  );

  const contextValue = useMemo(() => ({ show }), [show]);
  const title = t('description.select_wallet');

  const content = (
    <div className="flex flex-col gap-4">
      <div className="flex max-h-[50vh] flex-col gap-2 overflow-y-auto">
        {wallets.map((wallet) => (
          <button
            key={wallet.id}
            type="button"
            className="flex w-full cursor-pointer items-center rounded-xl border border-border bg-surface px-4 py-3 text-start text-foreground transition-colors duration-(--duration-fast) ease-(--ease-smooth-out) hover:border-accent hover:bg-surface-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent motion-reduce:transition-none"
            onClick={() => {
              close(wallet);
            }}
          >
            {isSafeIcon(wallet.icon) ? (
              <img className={`${iconClassName} object-contain`} src={wallet.icon} alt="" />
            ) : (
              <span
                aria-hidden
                className={`${iconClassName} flex items-center justify-center bg-surface-secondary text-muted`}
              >
                {wallet.name.charAt(0).toUpperCase()}
              </span>
            )}
            <span className="truncate">{wallet.name}</span>
          </button>
        ))}
      </div>
      <Button
        className="w-full"
        title="action.cancel"
        type="tertiary"
        onClick={() => {
          close();
        }}
      />
    </div>
  );

  return (
    <WalletModalContext.Provider value={contextValue}>
      {children}
      {isMobile ? (
        <Drawer.Backdrop isOpen={isOpen} onOpenChange={handleOpenChange}>
          <Drawer.Content placement="bottom">
            <Drawer.Dialog aria-label={title}>
              <Drawer.Handle />
              <Drawer.Header>
                <Drawer.Heading>{title}</Drawer.Heading>
              </Drawer.Header>
              <Drawer.Body>{content}</Drawer.Body>
            </Drawer.Dialog>
          </Drawer.Content>
        </Drawer.Backdrop>
      ) : (
        <Modal.Backdrop isOpen={isOpen} onOpenChange={handleOpenChange}>
          <Modal.Container placement="center" size="md">
            <Modal.Dialog aria-label={title}>
              <Modal.Header>
                <Modal.Heading>{title}</Modal.Heading>
              </Modal.Header>
              <Modal.Body>{content}</Modal.Body>
            </Modal.Dialog>
          </Modal.Container>
        </Modal.Backdrop>
      )}
    </WalletModalContext.Provider>
  );
};

export default WalletModalProvider;
